import React, { Component } from 'react';
import { get, omit } from 'lodash';
import classNames from 'classnames';
import scrollToComponent from 'react-scroll-to-component';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';

import {
  getSelectedForm,
  getFormValidationState,
  makeSelectVisibleTabIndexes,
} from 'APP_ROOT/selectors/form';

import { mapProperties } from '../../../utils/form';
import {
  getBlockingAssignedSections,
  hasIncompleteRequiredFieldsInVisibleTabs,
  getNextVisibleTabIndex,
  getPrevVisibleTabIndex,
  getNextTabTarget,
  getPrevTabTarget,
} from 'APP_ROOT/utils/evaluateTabConditions';
import clearHiddenTabData from 'APP_ROOT/utils/clearHiddenTabData';
import ScrollableNavigationSteps from './scrollable-navigation-steps/scrollable-navigation-steps';
import TabsNavigation from '../../form/tabs-navigation';
import emitter, { EventTypes } from 'APP_ROOT/utils/eventEmitter';
import propsHasChanged from 'APP_ROOT/utils/propsHasChanged';
import logChangedProps from 'APP_ROOT/utils/logChangedProps';
import {
  setTabsWithErrors,
  updateTabsWithError,
  validateCurrentTabFields,
} from '../../utils/tabs';

import ActionButtons from './ActionButtons';

import { translate } from '../../../i18next';

const DEFAULT_HEIGHT = 200;

class TabsHeader extends Component {
  constructor(props) {
    const { options = {}, isReviewer, properties = [] } = props;
    const { active: current = 0 } = options;
    super(props);
    const initialTabs = properties.map((_, index) => index);
    updateTabsWithError(initialTabs);
    this.state = {
      current: isReviewer ? 0 : current,
    };
  }

  componentDidMount() {
    const { isReviewer } = this.props;

    if (isReviewer) {
      window.addEventListener('scroll', this.spyScrolling);
    }

    emitter.on(EventTypes.SET_TAB, this.setTab);

    // Handle the form loading with the initial active tab hidden by conditions.
    this.ensureVisibleActiveTab();
  }

  componentWillUnmount() {
    const { isReviewer } = this.props;

    if (isReviewer) {
      window.removeEventListener('scroll', this.spyScrolling);
    }

    emitter.off(EventTypes.SET_TAB, this.setTab);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      propsHasChanged(nextProps, this.props) ||
      propsHasChanged(nextState, this.state)
    );
  }

  componentDidUpdate(prevProps) {
    const name =
      this.constructor.displayName || this.constructor.name || 'Component';
    logChangedProps(prevProps, this.props, name);

    // Clear the data of any tab that just became hidden (snapshot-level reset,
    // mirroring section/field conditionals) before moving the active tab off it.
    this.clearNewlyHiddenTabs(prevProps);

    // When a field change hides the currently active tab, move off of it.
    this.ensureVisibleActiveTab();
  }

  // Clears the fields of tabs that transitioned visible -> hidden since the last
  // render. Hidden tabs are not mounted, so their fields' own ConditionalAssert
  // never fires the auto-reset; we replicate that clearing here. No-op in
  // reviewer mode and in the form-builder editor.
  clearNewlyHiddenTabs = prevProps => {
    const { isReviewer, properties = [], visibleTabIndexes = [] } = this.props;
    const isEditor = get(this.props, 'settings.isEditor', false);

    if (isReviewer || isEditor) return;

    const prevVisible = (prevProps && prevProps.visibleTabIndexes) || [];
    if (!prevVisible.length) return;

    const newlyHidden = prevVisible.filter(
      index => !visibleTabIndexes.includes(index)
    );

    newlyHidden.forEach(index => {
      const tab = properties[index];
      if (tab && Array.isArray(tab.properties)) {
        clearHiddenTabData(tab.properties);
      }
    });
  };

  // Visible tab indexes are derived in mapState from Redux selectedForm.data,
  // so the nav recomputes whenever form data changes (e.g. toggling a trigger
  // field in the form-builder preview), independent of validation churn.
  getVisibleTabIndexes = () => {
    const { visibleTabIndexes = [] } = this.props;
    return visibleTabIndexes;
  };

  // Keeps the active tab on a visible tab. If the active tab becomes hidden by
  // conditions, advance to the next visible tab (fallback: previous, then
  // first). No-op in reviewer mode, where navigation is driven by scrolling.
  ensureVisibleActiveTab = () => {
    const { isReviewer, tab = 0 } = this.props;

    if (isReviewer) return;

    const visibleIndexes = this.getVisibleTabIndexes();

    if (visibleIndexes.length === 0 || visibleIndexes.includes(tab)) return;

    const nextVisible = getNextVisibleTabIndex(visibleIndexes, tab);
    const prevVisible = getPrevVisibleTabIndex(visibleIndexes, tab);
    const target =
      nextVisible !== undefined
        ? nextVisible
        : prevVisible !== undefined
        ? prevVisible
        : visibleIndexes[0];

    if (target !== tab) {
      this.syncTab(target);
    }
  };

  setTab = tab => this.setStep(null, tab);

  spyScrolling = () => {
    const { tabsRefs, formHeader } = this.props;
    const { current } = this.state;
    const sectionCount = tabsRefs.length;
    let currentScrolling = current;

    if (!sectionCount) return;

    const headerHeight =
      findDOMNode(formHeader)?.clientHeight || DEFAULT_HEIGHT;
    const topPos = window.pageYOffset + headerHeight;

    for (let i = 0; i < sectionCount; i++) {
      // Hidden tabs are not rendered, so their ref slot may be empty.
      const section = findDOMNode(tabsRefs[i]);
      if (section && topPos > section.offsetTop) {
        currentScrolling = i;
      }
    }

    this.setState({ current: currentScrolling });
  };

  syncTab = current => {
    const { settings = {}, validationState = {}, tab = 0 } = this.props;
    const { syncTab, validateFormFields } = settings;
    const { form: { submitRequested } = {} } = validationState;

    // Guard so auto-advance can never throw where the viewer is validation- or
    // settings-light (e.g. the form-builder preview).
    if (tab === current || typeof syncTab !== 'function') {
      return false;
    }

    window.scrollTo(0, 0);

    if (submitRequested) {
      syncTab(current);
      if (typeof validateFormFields === 'function') {
        validateFormFields(errors => this.setTabsWithErrors(tab, errors));
      }
    } else {
      syncTab(current);
      this.validateCurrentTabFields(tab);
    }
  };

  setStep = (e, current) => {
    const { isReviewer, tabsRefs, formHeader } = this.props;

    if (isReviewer) {
      const headerHeight =
        findDOMNode(formHeader)?.clientHeight || DEFAULT_HEIGHT;
      const offset = -headerHeight;
      scrollToComponent(tabsRefs[current], { align: 'top', offset });
    } else {
      this.syncTab(current);
    }
    this.setState({ current });
  };

  next = () => {
    const { tab = 0, properties = [], visibleTabIndexes = [] } = this.props;
    const target = getNextTabTarget(visibleTabIndexes, tab, properties.length);
    if (target !== undefined && target !== tab) {
      this.syncTab(target);
    }
  };

  prev = () => {
    const { tab = 0, visibleTabIndexes = [] } = this.props;
    const target = getPrevTabTarget(visibleTabIndexes, tab);
    if (target !== undefined && target !== tab) {
      this.syncTab(target);
    }
  };

  validateCurrentTabFields = current => {
    const {
      form: { validateFields },
      validationState: { tabsWithErrors },
    } = this.props;
    validateCurrentTabFields(current, validateFields, tabsWithErrors);
  };

  setTabsWithErrors = (current, errors = []) => {
    const {
      validationState: { tabsWithErrors },
    } = this.props;
    setTabsWithErrors(current, errors, tabsWithErrors);
  };

  computeSubmitBottomLabel = (isReviewer, isEditor) => {
    const { selectedForm } = this.props;
    let submitButtonLabel;
    if (isReviewer) {
      submitButtonLabel = translate(
        'containers.reports.reportViewer.addReview'
      );
    } else if (isEditor) {
      submitButtonLabel = translate('containers.reports.reportViewer.save');
    } else {
      submitButtonLabel = translate('containers.reports.reportViewer.submit');
    }

    if (selectedForm.isContributeReport) {
      const { visibleTabIndexes = [] } = this.props;
      // Keep the button on "Save Draft" while anything is still pending in a
      // VISIBLE tab: an incomplete CTR section, or an empty required field.
      // Hidden tabs/sections are excluded so they never block.
      const hasBlockingCtr =
        getBlockingAssignedSections(
          get(selectedForm, ['data', '__assignedSections'], []),
          get(
            selectedForm,
            ['template', 'formSchema', 'form', 'properties'],
            []
          ),
          get(selectedForm, 'data', {})
        ).length > 0;
      const hasIncompleteRequired = hasIncompleteRequiredFieldsInVisibleTabs(
        selectedForm,
        visibleTabIndexes
      );
      if (hasBlockingCtr || hasIncompleteRequired) {
        submitButtonLabel = translate('containers.reports.saveDraft');
      }
    }
    return submitButtonLabel;
  };

  render() {
    const {
      options = {},
      properties = [],
      tab = 0,
      validationState = {},
      loading = false, // eslint-disable-line
      reviewFormLoading = false,
      timezone,
      isReviewer = false,
      isViewer = false,
      canSubmit = true,
      submitDisable,
      tabsRefs = [],
      formHeader = [],
      actions = [],
      isPreviewMode,
      ...rest
    } = this.props;
    const { current } = this.state;
    const isEditor = get(rest, 'settings.isEditor', false);
    const isEditMode = get(rest, 'settings.isEditMode', false);

    const { form = {}, sections = {}, tabsWithErrors = [] } = validationState;

    const { submitRequested } = form;

    const visibleIndexes = this.getVisibleTabIndexes();

    const tabsClasses = properties.map((tab, index) => {
      // Hidden tabs are not rendered into the stepper at all (see the filtered
      // children below), so no tab-hidden/last-visible class is needed — the
      // real :last-child is the last visible tab. Only error/touched state
      // classes remain.
      const isTouched = get(sections[index], 'touched', false);

      if (isReviewer) {
        return '';
      }
      return classNames({
        'has-errors':
          (isTouched && !submitRequested && tabsWithErrors.includes(index)) ||
          (submitRequested && tabsWithErrors.includes(index)),
        'no-errors': isTouched && !tabsWithErrors.includes(index),
        'is-inactive': !isTouched && !submitRequested,
      });
    });

    const { showNavigation = true } = options;

    const submitButtonLabel = this.computeSubmitBottomLabel(
      isReviewer,
      isEditor
    );

    return (
      <div className="steps-navigation">
        <div className="navigation-control">
          {showNavigation && (
            <ScrollableNavigationSteps
              // antd Steps `current` is the position among RENDERED children.
              // Only visible tabs are rendered, so remap the active original
              // index to its position in the visible set (Redux `tab` and
              // setTab keep using original indices).
              current={Math.max(
                0,
                visibleIndexes.indexOf(isReviewer ? current : tab)
              )}
              progressDot
            >
              {properties
                .map(step => omit(step, ['disable']))
                // Map the FULL set first so each Step keeps its ORIGINAL index
                // (mapProperties derives index/className/setTab from array
                // position), then drop hidden tabs so the new scrollable steps
                // measure only the visible ones.
                .map(
                  mapProperties({
                    ...omit(rest, [
                      'changedFields',
                      'canSubmit',
                      'dispatch',
                      'disable',
                    ]),
                    total: properties.length,
                    classNames: tabsClasses,
                    tabsRefs,
                    formHeader,
                  })
                )
                .filter((_, index) => visibleIndexes.includes(index))}
            </ScrollableNavigationSteps>
          )}
        </div>
        <TabsNavigation
          showSubmitButton
          showNavigation={false}
          submitDisable={submitDisable}
          submitButtonLabel={submitButtonLabel}
          current={tab}
          onNext={this.next}
          onPrev={this.prev}
          totalSteps={properties.length}
          loading={loading || reviewFormLoading}
          isReviewer={isReviewer}
          isViewer={isViewer}
          canSubmit={canSubmit}
          isEditMode={isEditMode}
          isEditor={isEditor}
        />
        <ActionButtons actions={actions} isPreviewMode={isPreviewMode} />
      </div>
    );
  }
}

// Factory form: each instance gets its own memoized visible-tab selector keyed
// on the form data + this instance's `properties`, so the visibility walk is
// not recomputed on every unrelated Redux dispatch.
const makeMapStateToProps = () => {
  const selectVisibleTabIndexes = makeSelectVisibleTabIndexes();

  return (state, props) => {
    const { reviewForm: { loading: reviewFormLoading = false } = {} } = state;

    return {
      reviewFormLoading,
      validationState: getFormValidationState(state, props),
      selectedForm: getSelectedForm(state),
      visibleTabIndexes: selectVisibleTabIndexes(state, props),
    };
  };
};

export default connect(makeMapStateToProps)(TabsHeader);
